import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Button } from "components/DesignSystem/Button/Button";
import { FileIcon } from "components/design/FileIcon";
import { AnnotationDoc } from "components/DocumentAnnotation/DocumentAnnotationPanel";
import { CaretDown } from "components/icons/CaretDown";
import { DownloadIconV2 } from "components/icons/DownloadIconV2";
import { useDownloadFile } from "hooks/useDownloadFile";
import { pluralize } from "utils/pluralize";

type MobileReferenceDocumentsProps = {
  docs: AnnotationDoc[];
  groupId: string;
  onOpenDocument: (index: number) => void;
};

type ReferenceDocumentRowProps = {
  doc: AnnotationDoc;
  index: number;
  groupId: string;
  onOpenDocument: (index: number) => void;
};

const DOCUMENTS_ITEM_VALUE = "reference_documents";

const ReferenceDocumentRow = ({
  doc,
  index,
  groupId,
  onOpenDocument,
}: ReferenceDocumentRowProps) => {
  const { downloadFile } = useDownloadFile();

  const handleOpen = () => onOpenDocument(index);
  const handleDownload = () =>
    void downloadFile({ file_id: doc.id, group_id: groupId });

  return (
    <div className="t-flex t-items-center t-gap-2 t-rounded-lg t-border t-border-solid t-border-neutral-10 t-py-1 t-pl-1 t-pr-2">
      <Button
        type="button"
        customType="ghost"
        size="small"
        onClick={handleOpen}
        className="t-min-w-0 t-flex-1 !t-justify-start"
      >
        <span className="t-flex t-min-w-0 t-items-center t-gap-2">
          <FileIcon fileType={doc.fileType} width="24" height="24" />
          <span className="t-truncate t-text-body t-text-text-60">
            {doc.name}
          </span>
        </span>
      </Button>
      <Button
        type="button"
        customType="ghost_icon"
        size="small"
        onClick={handleDownload}
        aria-label={`Download ${doc.name}`}
      >
        <DownloadIconV2 />
      </Button>
    </div>
  );
};

export const MobileReferenceDocuments = ({
  docs,
  groupId,
  onOpenDocument,
}: MobileReferenceDocumentsProps) => {
  if (docs.length === 0) return null;

  return (
    <Accordion.Root
      type="single"
      collapsible
      defaultValue={DOCUMENTS_ITEM_VALUE}
    >
      <Accordion.RawItem
        value={DOCUMENTS_ITEM_VALUE}
        className="t-rounded-lg t-border t-border-solid t-border-neutral-10 t-bg-white"
      >
        <Accordion.RawTrigger className="all:unset t-group t-box-border t-flex t-w-full t-cursor-pointer t-items-center t-justify-between t-px-4 t-py-3 t-text-subtext t-text-text-60">
          {pluralize(docs.length, "Document", "Documents")} uploaded
          <span className="t-flex t-text-text-30 t-transition-transform t-duration-200 group-data-state-open:t-rotate-180">
            <CaretDown />
          </span>
        </Accordion.RawTrigger>
        <Accordion.RawContent className="t-flex t-flex-col t-gap-2 t-border-0 t-border-t t-border-solid t-border-neutral-10 t-p-3">
          {docs.map((doc, index) => (
            <ReferenceDocumentRow
              key={doc.id}
              doc={doc}
              index={index}
              groupId={groupId}
              onOpenDocument={onOpenDocument}
            />
          ))}
        </Accordion.RawContent>
      </Accordion.RawItem>
    </Accordion.Root>
  );
};
